import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  fetchCustomersRequest,
  fetchCustomersSuccess,
  fetchCustomersFailure,
  searchCustomersRequest,
  searchCustomersSuccess,
  searchCustomersFailure,
  fetchCustomerRequest,
  fetchCustomerSuccess,
  fetchCustomerFailure,
  createCustomerRequest,
  createCustomerSuccess,
  createCustomerFailure,
  updateCustomerRequest,
  updateCustomerSuccess,
  updateCustomerFailure,
  deleteCustomerRequest,
  deleteCustomerSuccess,
  deleteCustomerFailure,
  fetchCustomerAddressesRequest,
  fetchCustomerAddressesSuccess,
  fetchCustomerAddressesFailure,
  createCustomerAddressRequest,
  createCustomerAddressSuccess,
  createCustomerAddressFailure,
  deleteCustomerAddressRequest,
  deleteCustomerAddressSuccess,
  deleteCustomerAddressFailure,
  fetchCustomerNotesRequest,
  fetchCustomerNotesSuccess,
  fetchCustomerNotesFailure,
  createCustomerNoteRequest,
  createCustomerNoteSuccess,
  createCustomerNoteFailure,
  deleteCustomerNoteRequest,
  deleteCustomerNoteSuccess,
  deleteCustomerNoteFailure,
  fetchCustomerDashboardRequest,
  fetchCustomerDashboardSuccess,
  fetchCustomerDashboardFailure,
  importCustomersRequest,
  importCustomersSuccess,
  importCustomersFailure,
  exportCustomersRequest,
  exportCustomersSuccess,
  exportCustomersFailure,
} from '../slices/customer.slice';
import {
  getCustomers,
  searchCustomers,
  getCustomerById,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  getCustomerAddresses,
  createCustomerAddress,
  deleteCustomerAddress,
  getCustomerNotes,
  createCustomerNote,
  deleteCustomerNote,
  getCustomerDashboard,
  exportCustomers,
  importCustomers,
  SearchCustomersOptions,
} from '@/services/customerService';
import { 
  Customer, 
  CustomerAddress,
  CustomerNote,
  CreateCustomerDto,
  UpdateCustomerDto,
  CreateCustomerAddressDto,
  CreateCustomerNoteDto,
  FetchCustomersOptions 
} from '@/types/models/customer';
import { ApiError, PaginatedApiResponse, ApiResponse } from "@/lib/apiClient";

// Fetch Customers Saga
function* fetchCustomersSaga(action: PayloadAction<FetchCustomersOptions>) {
  try {
    const response: PaginatedApiResponse<Customer[]> = yield call(getCustomers, action.payload);
    
    yield put(fetchCustomersSuccess({
      list: response.data,
      page: response.pagination.page,
      total: response.pagination.total,
      pageSize: response.pagination.pageSize,
      totalPages: response.pagination.totalPages,
    }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchCustomersFailure(error));
    } else {
      yield put(
        fetchCustomersFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Search Customers Saga
function* searchCustomersSaga(action: PayloadAction<SearchCustomersOptions>) {
  try {
    const response: PaginatedApiResponse<Customer[]> = yield call(searchCustomers, action.payload);
    
    yield put(searchCustomersSuccess({
      list: response.data,
      page: response.pagination.page,
      total: response.pagination.total,
      pageSize: response.pagination.pageSize,
      totalPages: response.pagination.totalPages,
    }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(searchCustomersFailure(error));
    } else {
      yield put(
        searchCustomersFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Fetch Single Customer Saga
function* fetchCustomerSaga(action: PayloadAction<{ customerId: string }>) {
  try {
    const { customerId } = action.payload;
    const response: ApiResponse<Customer> = yield call(getCustomerById, customerId);
    
    yield put(fetchCustomerSuccess({ customer: response.data }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchCustomerFailure(error));
    } else {
      yield put(
        fetchCustomerFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Create Customer Saga
function* createCustomerSaga(action: PayloadAction<CreateCustomerDto>) {
  try {
    const response: ApiResponse<Customer> = yield call(createCustomer, action.payload);
    
    yield put(createCustomerSuccess({ customer: response.data }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(createCustomerFailure(error));
    } else {
      yield put(
        createCustomerFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Update Customer Saga
function* updateCustomerSaga(action: PayloadAction<{ customerId: string; customer: UpdateCustomerDto }>) {
  try {
    const { customerId, customer } = action.payload;
    const response: ApiResponse<Customer> = yield call(updateCustomer, customerId, customer);
    
    yield put(updateCustomerSuccess({ customer: response.data }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(updateCustomerFailure(error));
    } else {
      yield put(
        updateCustomerFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Delete Customer Saga
function* deleteCustomerSaga(action: PayloadAction<{ customerId: string }>) {
  try {
    const { customerId } = action.payload;
    yield call(deleteCustomer, customerId);
    
    yield put(deleteCustomerSuccess({ customerId }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(deleteCustomerFailure(error));
    } else {
      yield put(
        deleteCustomerFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Fetch Customer Addresses Saga
function* fetchCustomerAddressesSaga(action: PayloadAction<{ customerId: string; page?: number; pageSize?: number }>) {
  try {
    const { customerId, page, pageSize } = action.payload;
    const response: PaginatedApiResponse<CustomerAddress[]> = yield call(getCustomerAddresses, customerId, page, pageSize);
    
    yield put(fetchCustomerAddressesSuccess({ addresses: response.data }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchCustomerAddressesFailure(error));
    } else {
      yield put(
        fetchCustomerAddressesFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Create Customer Address Saga
function* createCustomerAddressSaga(action: PayloadAction<{ customerId: string; address: CreateCustomerAddressDto }>) {
  try {
    const { customerId, address } = action.payload;
    const response: ApiResponse<CustomerAddress> = yield call(createCustomerAddress, customerId, address);
    
    yield put(createCustomerAddressSuccess({ address: response.data }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(createCustomerAddressFailure(error));
    } else {
      yield put(
        createCustomerAddressFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Delete Customer Address Saga
function* deleteCustomerAddressSaga(action: PayloadAction<{ customerId: string; addressId: string }>) {
  try {
    const { customerId, addressId } = action.payload;
    yield call(deleteCustomerAddress, customerId, addressId);
    
    yield put(deleteCustomerAddressSuccess({ addressId }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(deleteCustomerAddressFailure(error));
    } else {
      yield put(
        deleteCustomerAddressFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Fetch Customer Notes Saga
function* fetchCustomerNotesSaga(action: PayloadAction<{ customerId: string; page?: number; pageSize?: number }>) {
  try {
    const { customerId, page, pageSize } = action.payload;
    const response: PaginatedApiResponse<CustomerNote[]> = yield call(getCustomerNotes, customerId, page, pageSize);
    
    yield put(fetchCustomerNotesSuccess({ notes: response.data }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchCustomerNotesFailure(error));
    } else {
      yield put(
        fetchCustomerNotesFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Create Customer Note Saga
function* createCustomerNoteSaga(action: PayloadAction<{ customerId: string; note: CreateCustomerNoteDto }>) {
  try {
    const { customerId, note } = action.payload;
    const response: ApiResponse<CustomerNote> = yield call(createCustomerNote, customerId, note);
    
    yield put(createCustomerNoteSuccess({ note: response.data }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(createCustomerNoteFailure(error));
    } else {
      yield put(
        createCustomerNoteFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Delete Customer Note Saga
function* deleteCustomerNoteSaga(action: PayloadAction<{ customerId: string; noteId: string }>) {
  try {
    const { customerId, noteId } = action.payload;
    yield call(deleteCustomerNote, customerId, noteId);
    
    yield put(deleteCustomerNoteSuccess({ noteId }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(deleteCustomerNoteFailure(error));
    } else {
      yield put(
        deleteCustomerNoteFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Fetch Customer Dashboard Saga
function* fetchCustomerDashboardSaga() {
  try {
    const response: ApiResponse<any> = yield call(getCustomerDashboard);
    
    yield put(fetchCustomerDashboardSuccess({
      totalCustomers: response.data.totalCustomers,
      newCustomers: response.data.newCustomers,
      repeatCustomers: response.data.repeatCustomers,
      avgOrderValue: response.data.avgOrderValue,
    }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchCustomerDashboardFailure(error));
    } else {
      yield put(
        fetchCustomerDashboardFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Import Customers Saga
function* importCustomersSaga(action: PayloadAction<{ file: File }>) {
  try {
    const { file } = action.payload;
    const response: ApiResponse<any> = yield call(importCustomers, file);
    
    yield put(importCustomersSuccess({
      imported: response.data.imported,
      updated: response.data.updated,
    }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(importCustomersFailure(error));
    } else {
      yield put(
        importCustomersFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Export Customers Saga
function* exportCustomersSaga() {
  try {
    yield call(exportCustomers);
    
    yield put(exportCustomersSuccess());
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(exportCustomersFailure(error));
    } else {
      yield put(
        exportCustomersFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

export default function* customersRootSaga() {
  yield takeLatest(fetchCustomersRequest.type, fetchCustomersSaga);
  yield takeLatest(searchCustomersRequest.type, searchCustomersSaga);
  yield takeLatest(fetchCustomerRequest.type, fetchCustomerSaga);
  yield takeEvery(createCustomerRequest.type, createCustomerSaga);
  yield takeEvery(updateCustomerRequest.type, updateCustomerSaga);
  yield takeEvery(deleteCustomerRequest.type, deleteCustomerSaga);
  yield takeLatest(fetchCustomerAddressesRequest.type, fetchCustomerAddressesSaga);
  yield takeEvery(createCustomerAddressRequest.type, createCustomerAddressSaga);
  yield takeEvery(deleteCustomerAddressRequest.type, deleteCustomerAddressSaga);
  yield takeLatest(fetchCustomerNotesRequest.type, fetchCustomerNotesSaga);
  yield takeEvery(createCustomerNoteRequest.type, createCustomerNoteSaga);
  yield takeEvery(deleteCustomerNoteRequest.type, deleteCustomerNoteSaga);
  yield takeLatest(fetchCustomerDashboardRequest.type, fetchCustomerDashboardSaga);
  yield takeEvery(importCustomersRequest.type, importCustomersSaga);
  yield takeLatest(exportCustomersRequest.type, exportCustomersSaga);
}